/**
 * Dipendenti dello studio divisi per sede (titolare e admin). La sede è solo organizzativa:
 * `id: null` è la sede principale (il profilo dell'associato), gli altri id sono sedi secondarie.
 */
import type { DipendenteStudio, SedeStudio } from '../services/dipendentiService';

export interface GruppoSede {
  sede: SedeStudio;
  dipendenti: DipendenteStudio[];
}

const PRINCIPALE_GENERICA: SedeStudio = { id: null, nome: 'Sede principale' };

const chiave = (id: string | null | undefined) => (id ? `id:${id}` : 'principale');

/**
 * Gruppi non vuoti: prima la principale, poi le altre in ordine di nome. Un dipendente senza sede
 * va nella principale; una sede assente dall'elenco prende il nome arrivato con il dipendente.
 */
export function raggruppaPerSede(dipendenti: DipendenteStudio[], sedi?: SedeStudio[] | null): GruppoSede[] {
  const note = new Map<string, SedeStudio>();
  (sedi || []).forEach((s) => { if (!note.has(chiave(s.id))) note.set(chiave(s.id), s); });
  const gruppi = new Map<string, GruppoSede>();
  dipendenti.forEach((d) => {
    const id = d.sede?.id || null;
    const k = chiave(id);
    let gruppo = gruppi.get(k);
    if (!gruppo) {
      const sede = note.get(k)
        || (d.sede?.nome ? { id, nome: d.sede.nome } : id ? { id, nome: 'Altra sede' } : PRINCIPALE_GENERICA);
      gruppo = { sede, dipendenti: [] };
      gruppi.set(k, gruppo);
    }
    gruppo.dipendenti.push(d);
  });
  return Array.from(gruppi.values()).sort((a, b) => {
    if (!a.sede.id !== !b.sede.id) return a.sede.id ? 1 : -1;
    return a.sede.nome.localeCompare(b.sede.nome, 'it');
  });
}

const normalizza = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Ricerca per nome e cognome (anche «cognome nome»), senza maiuscole né accenti. */
export function filtraDipendenti(dipendenti: DipendenteStudio[], testo: string): DipendenteStudio[] {
  const q = normalizza(testo || '');
  if (!q) return dipendenti;
  return dipendenti.filter((d) => {
    const nome = normalizza(d.nome || '');
    const cognome = normalizza(d.cognome || '');
    return `${nome} ${cognome}`.includes(q) || `${cognome} ${nome}`.includes(q);
  });
}

/** Valore di un <select> per la sede: la principale è la stringa vuota. */
export const valoreSede = (id: string | null | undefined): string => id || '';

export const sedeDaValore = (valore: string): string | null => valore || null;

/** Più di una sede selezionabile (elenco del server) o dipendenti già in sedi diverse. */
export const conPiuSedi = (sedi: SedeStudio[] | null | undefined, gruppi: GruppoSede[]): boolean =>
  (sedi?.length || 0) > 1 || gruppi.length > 1;
